/**
 * ShowFileRenderer — renders a <SHOW_FILE> tag.
 *
 * Two visual outcomes:
 *   images   → <ChatImageChunk> (with transparency detection + load state)
 *   anything → <FileTile>       (pdf / audio / video / pptx / xlsx /
 *                                markdown / generic)
 *
 * Video intentionally goes through FileTile like every other non-image
 * media type. Clicking opens FullPageMediaViewer which has a full
 * <VideoPlayer>. This matches the audio / PDF pattern — one visual
 * vocabulary for all file attachments.
 *
 * Resolver context (per call):
 *   - `blobNameToUrl`: dictionary mapping blob name (the SHOW_FILE inner
 *     text) to either a signed URL string or a `{ url, filename,
 *     mimeType, fileSize }` object.
 *   - `messageIndex`: stable index used to build the media id the
 *     slideshow listens for (see MediaViewerPanel / FullPageMediaViewer).
 *   - `mediaIndex`: assigned at the AST level by remarkCellCogTags based
 *     on document order of SHOW_FILE tags. Stable across re-renders.
 *
 * Pending / missing variants:
 *   Some tags resolve to `blobNameToUrl[blobName] === undefined`. What
 *   to show depends on the surface:
 *     - "spinner" (streaming chat): "Loading media..." with a spinner.
 *       The resolution is in-flight; show an optimistic placeholder.
 *     - "missing" (finalized chat): a neutral warning tile with the
 *       filename. The resolution failed (file expired / moved / deleted);
 *       showing nothing would be misleading.
 *     - "none" (posts / file viewer / public docs): render null. Those
 *       surfaces don't resolve SHOW_FILE tags; the expected behavior is
 *       to drop them silently.
 */
import { WarningOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useTransparencyDetection } from '../../../../hooks/useTransparencyDetection';
import FileTile from '../../../Chat/ChatMessageContent/FileTile/FileTile';
import { Download } from 'lucide-react';
import { downloadFile } from '../../../../utils/downloadFile';
import './ShowFileRenderer.css';

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff)$/i;

const isImageExtension = (filename) => IMAGE_EXT_RE.test(filename || '');

/**
 * ChatImageChunk — standalone image renderer that owns image-specific
 * hooks (transparency detection, load state). Kept as a child component
 * so its hooks only fire on the image path; non-image SHOW_FILE tags
 * never pay that cost.
 *
 * Optional `onOpen` (Chat Files gallery, CEL-3473): called with `{ id, url }`
 * INSTEAD of dispatching imageClick + mediaClick, for a host that opens its
 * own list of files. Callers that don't pass it are unchanged.
 */
export const ChatImageChunk = React.memo(({ src, blobName, mediaId, onOpen }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  // An image shorter than the reserved box (icons, small sprites) shrinks the box to its own size once it loads,
  // instead of sitting alone at the top of a tall empty area (Nitish, Nov 2 2026). Taller images keep the fixed box.
  const [fitsImage, setFitsImage] = useState(false);
  // A small image (badges, icons) gets its download button beside it, not on top of it (Nitish, Nov 2 2026).
  const [isSmall, setIsSmall] = useState(false);
  const { hasTransparency } = useTransparencyDetection(loaded ? src : null, blobName);

  const handleImageLoad = (e) => {
    const img = e && e.currentTarget;
    if (img) {
      const r = img.getBoundingClientRect();
      if (r.height > 0 && (r.height < 64 || r.width < 96)) setIsSmall(true);
    }
    const box = img && img.parentElement && img.parentElement.querySelector('.message-image-placeholder');
    if (img && box && box.clientHeight > 0 && img.getBoundingClientRect().height < box.clientHeight - 1) {
      setFitsImage(true);
    }
    setLoaded(true);
    // (Removed 2026-06-21: the `chatImageLoaded` dispatch. Its only consumer was
    // a ChatWindow handler that re-pinned scroll to the bottom on every image
    // load — jamming image-heavy chats. Image placeholders reserve fixed height
    // (see ChatMessageContent.css) so loads don't shift scroll anyway. Listener
    // removed; with no consumers left, we stop firing the event.)
  };

  const handleImageError = () => {
    setError(true);
  };

  const handleImageClick = () => {
    if (onOpen) {
      onOpen({ id: mediaId, url: src });
      return;
    }
    window.dispatchEvent(new CustomEvent('imageClick', { detail: src }));
    window.dispatchEvent(
      new CustomEvent('mediaClick', { detail: { id: mediaId, url: src } }),
    );
  };

  const showCheckerboard = loaded && hasTransparency;

  // Download button (Nitish, Sep 19 2026): every SHOW_FILE render, images included, saves with one click.
  const filename = (blobName || '').split('/').pop() || 'image';
  const downloadButton = loaded && !error ? (
    <button
      type="button"
      className="message-image-download"
      title="Download"
      aria-label={`Download ${filename}`}
      onClick={(e) => {
        e.stopPropagation();
        downloadFile(src, filename);
      }}
    >
      <Download size={14} strokeWidth={2.25} />
    </button>
  ) : null;

  if (showCheckerboard) {
    return (
      <div className={`message-image-container has-transparency${isSmall ? ' small-image' : ''}`}>
        {!error && (
          <img
            src={src}
            alt={blobName}
            className="message-image loaded transparent-flow"
            onLoad={handleImageLoad}
            onError={handleImageError}
            onClick={handleImageClick}
          />
        )}
        {downloadButton}
      </div>
    );
  }

  return (
    <div className={`message-image-container${fitsImage ? ' fits-image' : ''}${isSmall ? ' small-image' : ''}`}>
      <div className={`message-image-placeholder ${loaded ? 'loaded' : ''}`}></div>
      {!error && (
        <img
          src={src}
          alt={blobName}
          className={`message-image ${loaded ? 'loaded' : 'loading'}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          onClick={handleImageClick}
        />
      )}
      {downloadButton}
    </div>
  );
});
ChatImageChunk.displayName = 'CellCogMarkdown.ChatImageChunk';

/**
 * Pending placeholder for streaming — the blob's URL is being resolved
 * on the server. Shows a spinner + "Loading media..." text.
 */
const PendingSpinner = React.memo(() => (
  <div className="show-file-pending">
    <div className="show-file-pending-spinner" />
    <span className="show-file-pending-text">Loading media...</span>
  </div>
));
PendingSpinner.displayName = 'CellCogMarkdown.PendingSpinner';

/**
 * Missing-file tile for finalized chat — the blob resolution failed.
 * Neutral warning tone (not alarming). Shows the filename referenced by
 * the tag so the user has some context about what was expected.
 */
const MissingFileTile = React.memo(({ filename }) => (
  <div className="show-file-missing" role="status" aria-live="polite">
    <div className="show-file-missing-icon" aria-hidden="true">
      <WarningOutlined />
    </div>
    <div className="show-file-missing-body">
      <div className="show-file-missing-title">File not available</div>
      {filename && (
        <div className="show-file-missing-filename" title={filename}>
          {filename}
        </div>
      )}
      <div className="show-file-missing-hint">
        This file may have expired or been moved.
      </div>
    </div>
  </div>
));
MissingFileTile.displayName = 'CellCogMarkdown.MissingFileTile';

/**
 * Read a well-formed file descriptor off the resolver value, which may
 * be a plain signed-URL string (legacy) or the newer object shape.
 */
const normalizeFileData = (fileData, blobName) => {
  if (!fileData) return null;
  if (typeof fileData === 'string') {
    return {
      url: fileData,
      filename: blobName,
      mimeType: null,
      fileSize: 0,
    };
  }
  return {
    url: fileData.url,
    filename: fileData.filename || blobName,
    mimeType: fileData.mimeType || null,
    fileSize: fileData.fileSize || 0,
  };
};

const isImagePayload = (fd, blobName) => {
  if (fd?.mimeType) return fd.mimeType.startsWith('image/');
  return isImageExtension(fd?.filename || blobName);
};

const ShowFileRenderer = ({
  fileUri,
  blobNameToUrl,
  messageIndex,
  mediaIndex,
  pendingVariant = 'none',
  // channel_media_viewer_prd.md F1: channel surfaces register their media under message-id-keyed
  // prefixes (`ch-<msgId>` / `thr-<msgId>`, CEL-795 D3) — the dispatched click id must match the
  // registered set or the lookup silently no-ops. Default 'msg' keeps regular chats byte-identical.
  mediaIdPrefix = 'msg',
}) => {
  const blobName = (fileUri || '').trim();
  if (!blobName) return null;

  // No resolver at all → this surface doesn't handle SHOW_FILE tags
  // (posts, file viewer, public docs). Render nothing.
  if (!blobNameToUrl) return null;

  const fd = normalizeFileData(blobNameToUrl[blobName], blobName);

  // Unresolved blob — show the appropriate placeholder per surface.
  if (!fd || !fd.url) {
    if (pendingVariant === 'spinner') {
      return <PendingSpinner />;
    }
    if (pendingVariant === 'missing') {
      // Prefer the filename portion of the blob name if it contains path
      // separators; otherwise show the blob name as-is.
      const displayName = blobName.includes('/')
        ? blobName.split('/').pop()
        : blobName;
      return <MissingFileTile filename={displayName} />;
    }
    // pendingVariant === 'none'
    return null;
  }

  const mediaId = `${mediaIdPrefix}-${messageIndex ?? -1}-media-${mediaIndex ?? 0}`;

  if (isImagePayload(fd, blobName)) {
    return <ChatImageChunk src={fd.url} blobName={blobName} mediaId={mediaId} />;
  }

  // Everything else — including video — goes through FileTile. The file
  // tile's per-type config handles the purple video badge, the audio
  // waveform, the PDF fanned pages, etc. Clicking dispatches mediaClick
  // and the full-page viewer handles inline playback for video/audio.
  return (
    <FileTile
      fileInfo={{
        url: fd.url,
        filename: fd.filename,
        mimeType: fd.mimeType || 'application/octet-stream',
        fileSize: fd.fileSize,
      }}
      mediaId={mediaId}
      messageIndex={messageIndex}
      mediaIndex={mediaIndex}
      density="strip"
    />
  );
};

export default React.memo(ShowFileRenderer);
